import React, { useEffect, useMemo, useRef, useState } from "react";
import { MetaTags } from "react-meta-tags";
import { Container, Row, Col, Card, CardBody, Button, Form, Label, Input } from "reactstrap";
import QRCodeStyling from "qr-code-styling";
import { allRoutes, authRoutes, publicRoutes } from "../../../routes/allRoutes";
import { getPublicBaseUrl } from "../../../helpers/url";
import { FaQrcode } from "react-icons/fa";

// Utilidad para obtener la URL base desde .env (quitando "/api")
const getBaseUrl = () => {
  const URL = process.env.REACT_APP_API_URL;
  if (!URL) return window.location.origin;
  const url = URL.split("/api");
  return url[0];
};

// Obtiene una lista de rutas v�lidas del sistema
const getSystemPaths = () => {
  const extractPaths = (routes) =>
    routes
      .map((r) => r?.path)
      .filter(Boolean)
      .filter((p) => p !== "/" && p !== "*" && p.startsWith("/"));

  const paths = [
    // Solo authRoutes seg�n lo indicado
    ...extractPaths(authRoutes),
  ];
  return Array.from(new Set(paths)).sort();
};

const hasAdminAccess = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user"));
    const groups = String(user?.groups?.[0]?.[0] || user?.groups || "");
    return groups.includes("SuperAdmin") || groups.includes("Administrador");
  } catch (e) {
    return false;
  }
};

const QRGenerator = () => {
  const [selectedPath, setSelectedPath] = useState("/landingPage");
  const [customPath, setCustomPath] = useState("");
  const [useCustom, setUseCustom] = useState(false);
  const [format, setFormat] = useState("png");
  const [size, setSize] = useState(400);
  const qrRef = useRef(null);
  const [style, setStyle] = useState("clasico");
  const [fgColor, setFgColor] = useState("#000000");
  const [bgColor, setBgColor] = useState("#ffffff");
  const [copied, setCopied] = useState(false);
  const qrCode = useRef(null);
  const [dotType, setDotType] = useState("square");
  const [cornerSquareType, setCornerSquareType] = useState("square");
  const [cornerDotType, setCornerDotType] = useState("dot");
  const [hideBackgroundDots, setHideBackgroundDots] = useState(true);
  const [logoSrc, setLogoSrc] = useState("");
  const [logoSize, setLogoSize] = useState(0.4);
  const [animate, setAnimate] = useState(false);

  // Inserto variables derivadas: rutas, baseUrl y URL completa
  const paths = useMemo(() => getSystemPaths(), []);
  const baseUrl = useMemo(() => getPublicBaseUrl(), []);
  const fullUrl = useMemo(() => {
    const path = useCustom ? customPath : selectedPath;
    if (!path) return baseUrl;
    const normalized = path.startsWith("/") ? path : `/${path}`;
    const cleanBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
    return `${cleanBase}${normalized}`;
  }, [baseUrl, selectedPath, useCustom, customPath]);

  useEffect(() => {
    switch (style) {
      case "clasico":
        setFgColor("#000000");
        setBgColor("#ffffff");
        break;
      case "suave":
        setFgColor("#1f3a8a");
        setBgColor("#eef2ff");
        break;
      case "oscuro":
        setFgColor("#ffffff");
        setBgColor("#111827");
        break;
      case "verde":
        setFgColor("#0f766e");
        setBgColor("#ecfdf5");
        break;
      case "rojo":
        setFgColor("#b91c1c");
        setBgColor("#fef2f2");
        break;
      default:
        setFgColor("#000000");
        setBgColor("#ffffff");
    }
  }, [style]);

  useEffect(() => {
    if (!qrCode.current) {
      qrCode.current = new QRCodeStyling({
        data: fullUrl,
        width: size,
        height: size,
        qrOptions: { errorCorrectionLevel: "H" },
        dotsOptions: { color: fgColor, type: dotType },
        cornersSquareOptions: { type: cornerSquareType },
        cornersDotOptions: { type: cornerDotType },
        backgroundOptions: { color: bgColor },
        image: logoSrc || undefined,
        imageOptions: { crossOrigin: "anonymous", margin: 4, hideBackgroundDots, imageSize: logoSize },
      });
      if (qrRef.current) {
        qrCode.current.append(qrRef.current);
      }
    }
  }, []);

  useEffect(() => {
    if (qrCode.current) {
      qrCode.current.update({
        data: fullUrl,
        width: size,
        height: size,
        qrOptions: { errorCorrectionLevel: "H" },
        dotsOptions: { color: fgColor, type: dotType },
        cornersSquareOptions: { type: cornerSquareType },
        cornersDotOptions: { type: cornerDotType },
        backgroundOptions: { color: bgColor },
        image: logoSrc || undefined,
        imageOptions: { crossOrigin: "anonymous", margin: 4, hideBackgroundDots, imageSize: logoSize },
      });
    }
  }, [fullUrl, size, fgColor, bgColor, dotType, cornerSquareType, cornerDotType, logoSrc, hideBackgroundDots, logoSize]);

  useEffect(() => {
    // Normaliza customPath si se inicia con baseUrl
    if (useCustom && customPath.startsWith(baseUrl)) {
      const remainder = customPath.replace(baseUrl, "");
      setCustomPath(remainder || "/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useCustom]);

  if (!hasAdminAccess()) {
    return (
      <div className="page-content">
        <MetaTags>
          <title>Acceso Restringido | Generador QR</title>
        </MetaTags>
              <style>{`
        .qr-preview { display:flex; flex-direction:column; align-items:center; }
        .qr-frame { position:relative; padding:18px; border-radius:16px; background:linear-gradient(180deg, rgba(255,255,255,0.75), rgba(255,255,255,0.55)); box-shadow:0 8px 24px rgba(17,24,39,0.12), inset 0 1px 0 rgba(255,255,255,0.6); backdrop-filter:saturate(1.1) blur(8px); }
        .qr-frame::before { content:""; position:absolute; inset:-2px; border-radius:inherit; background:radial-gradient(120% 120% at 0% 0%, rgba(99,102,241,0.25) 0%, rgba(99,102,241,0) 60%), radial-gradient(120% 120% at 100% 100%, rgba(16,185,129,0.25) 0%, rgba(16,185,129,0) 60%); filter:blur(12px); z-index:0; }
        .qr-canvas { position:relative; z-index:1; }
        @keyframes popIn { from { transform:scale(0.98); opacity:0; } to { transform:scale(1); opacity:1; } }
        .pop-in { animation:popIn 380ms ease-out; }
      `}</style>\n      <Container fluid>
          <Row className="justify-content-center mt-5">
            <Col lg={6}>
              <Card className="text-center">
                <CardBody>
                  <h4 className="mb-2">403 - Acceso Restringido</h4>
                  <p className="text-muted mb-3">
                    Este módulo está disponible solo para usuarios con rol <b>Administrador</b> o <b>SuperAdmin</b>.
                  </p>
                  <Button color="primary" onClick={() => (window.location.href = "/dashboard")}>Ir al Dashboard</Button>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  const handleDownload = (mime) => {
    if (!qrCode.current) return;
    const safePath = (useCustom ? customPath : selectedPath).replace(/\//g, "-") || "home";
    const ext = mime === "image/png" ? "png" : "jpeg";
    qrCode.current.download({ name: `qr-${safePath}`, extension: ext });
  };

  // Paletas de colores rápidas para facilitar selección
  const fgPalette = ["#000000", "#1f3a8a", "#2563eb", "#0f766e", "#b91c1c", "#374151", "#111827", "#f59e0b"];
  const bgPalette = ["#ffffff", "#eef2ff", "#f0f9ff", "#ecfdf5", "#fef2f2", "#f3f4f6", "#111827", "#fff7ed"];

  return (
    <div className="page-content">
      <MetaTags>
        <title>Generador QR | Administración</title>
      </MetaTags>
            <style>{`
        .qr-preview { display:flex; flex-direction:column; align-items:center; }
        .qr-frame { position:relative; padding:18px; border-radius:16px; background:linear-gradient(180deg, rgba(255,255,255,0.75), rgba(255,255,255,0.55)); box-shadow:0 8px 24px rgba(17,24,39,0.12), inset 0 1px 0 rgba(255,255,255,0.6); backdrop-filter:saturate(1.1) blur(8px); }
        .qr-frame::before { content:""; position:absolute; inset:-2px; border-radius:inherit; background:radial-gradient(120% 120% at 0% 0%, rgba(99,102,241,0.25) 0%, rgba(99,102,241,0) 60%), radial-gradient(120% 120% at 100% 100%, rgba(16,185,129,0.25) 0%, rgba(16,185,129,0) 60%); filter:blur(12px); z-index:0; }
        .qr-canvas { position:relative; z-index:1; }
        @keyframes popIn { from { transform:scale(0.98); opacity:0; } to { transform:scale(1); opacity:1; } }
        .pop-in { animation:popIn 380ms ease-out; }
      `}</style>
       <Container fluid>
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <div className="d-flex align-items-center mb-3">
                  <FaQrcode size={28} className="me-2" />
                  <h4 className="mb-0">Generador de Códigos QR</h4>
                </div>
                <p className="text-muted">Genera códigos QR para rutas públicas del sistema y personaliza su estilo.</p>

                                <Row className="g-4">
                  <Col lg={6}>
                    <h6 className="text-uppercase text-muted mb-3">Destino</h6>
                    <Form>
                      <div className="mb-3">
                        <Label className="form-label">Seleccionar URL del sistema</Label>
                        <Input bsSize="sm" type="select" value={selectedPath} onChange={(e) => setSelectedPath(e.target.value)}>
                          {paths.map((p) => (
                            <option key={p} value={p}>{p}</option>
                          ))}
                        </Input>
                      </div>
                      <div className="mb-3">
                        <Label className="form-label">Usar URL personalizada</Label>
                        <div className="d-flex align-items-center gap-2">
                          <Input type="checkbox" className="form-check-input" id="customCheck" checked={useCustom} onChange={(e) => setUseCustom(e.target.checked)} />
                          <Input bsSize="sm" type="text" placeholder="/mi-ruta" value={customPath} disabled={!useCustom} onChange={(e) => setCustomPath(e.target.value)} />
                        </div>
                        <small className="text-muted">Base: {baseUrl}</small>
                      </div>
                      <div className="mb-4">
                        <Label className="form-label">URL generada</Label>
                        <Input bsSize="sm" type="text" value={fullUrl} readOnly />
                      </div>

                      <h6 className="text-uppercase text-muted mb-3">Apariencia</h6>
                      <Row className="g-3">
                        <Col md={6}>
                          <Label className="form-label">Color principal</Label>
                          <Input bsSize="sm" className="form-control-color" type="color" value={fgColor} onChange={(e) => setFgColor(e.target.value)} />
                          <div className="d-flex flex-wrap gap-2 mt-2">
                            {fgPalette.map((c) => (
                              <button
                                key={c}
                                type="button"
                                className="btn p-0 border"
                                style={{ backgroundColor: c, width: 24, height: 24, borderRadius: 6 }}
                                onClick={() => setFgColor(c)}
                                aria-label={c}
                                title={c}
                              />
                            ))}
                          </div>
                          <small className="text-muted">{fgColor.toUpperCase()}</small>
                        </Col>
                        <Col md={6}>
                          <Label className="form-label">Fondo</Label>
                          <Input bsSize="sm" className="form-control-color" type="color" value={bgColor} onChange={(e) => setBgColor(e.target.value)} />
                          <div className="d-flex flex-wrap gap-2 mt-2">
                            {bgPalette.map((c) => (
                              <button
                                key={c}
                                type="button"
                                className="btn p-0 border"
                                style={{ backgroundColor: c, width: 24, height: 24, borderRadius: 6 }}
                                onClick={() => setBgColor(c)}
                                aria-label={c}
                                title={c}
                              />
                            ))}
                          </div>
                          <small className="text-muted">{bgColor.toUpperCase()}</small>
                        </Col>
                        <Col md={6}>
                          <Label className="form-label">Puntuaci&oacute;n (puntos)</Label>
                          <Input bsSize="sm" type="select" value={dotType} onChange={(e) => setDotType(e.target.value)}>
                            <option value="square">Cuadrado</option>
                            <option value="dots">Puntos</option>
                          </Input>
                        </Col>
                        <Col md={6}>
                          <Label className="form-label">Esquinas</Label>
                          <Input bsSize="sm" type="select" value={cornerSquareType} onChange={(e) => setCornerSquareType(e.target.value)}>
                            <option value="square">Rectas</option>
                            <option value="extra-rounded">Redondeadas</option>
                          </Input>
                        </Col>
                        <Col md={6}>
                          <Label className="form-label">Punto de esquina</Label>
                          <Input bsSize="sm" type="select" value={cornerDotType} onChange={(e) => setCornerDotType(e.target.value)}>
                            <option value="dot">Punto</option>
                            <option value="square">Cuadrado</option>
                          </Input>
                        </Col>
                        <Col md={6}>
                          <Label className="form-label">Logo</Label>
                          <Input bsSize="sm" type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) { const url = URL.createObjectURL(f); setLogoSrc(url); } }} />
                          <div className="form-check mt-2">
                            <Input type="checkbox" className="form-check-input" id="hideDots" checked={hideBackgroundDots} onChange={(e) => setHideBackgroundDots(e.target.checked)} />
                            <Label className="form-check-label" htmlFor="hideDots">Ocultar puntos bajo el logo</Label>
                          </div>
                        </Col>
                                                <Col md={6}>
                          <Label className="form-label">Tamaño</Label>
                          <Input bsSize="sm"
                            type="number"
                            min={64}
                            max={1024}
                            value={size}
                            onChange={(e) => setSize(Number(e.target.value))}
                          />
                        </Col>
                        <Col md={6}>
                          <Label className="form-label">Tamaño del logo</Label>
                          <Input type="range" min={0.2} max={0.9} step={0.05} value={logoSize} onChange={(e) => setLogoSize(Number(e.target.value))} />
                          <small className="text-muted">Proporci&oacute;n: {Math.round(logoSize * 100)}%</small>
                        </Col>
                      </Row>
                    </Form>
                  </Col>

                  <Col lg={6}>
                    <div className="qr-preview">
                      <div className={"qr-frame " + (animate ? "pop-in" : "")}>
                        <div ref={qrRef} className="qr-canvas" style={{ width: size, height: size }} />
                      </div>
                      <small className="text-muted mt-2">Vista previa del c&oacute;digo QR</small>
                    </div>
                    <div className="d-flex flex-wrap justify-content-center gap-2 mt-3">
                      <Button size="sm" color="primary" className="px-3" onClick={() => handleDownload("image/png")}>Descargar PNG</Button>
                      <Button size="sm" color="warning" className="px-3" onClick={() => handleDownload("image/jpeg")}>Descargar JPG</Button>
                      <Button size="sm" color="dark" outline className="px-3" onClick={() => { navigator.clipboard.writeText(fullUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>Copiar URL</Button>
                    </div>
                    {copied && <small className="text-success mt-2">URL copiada</small>}
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>


        </Row>
      </Container>
    </div>
  );
};

export default QRGenerator;
